/**
 * Ephemeral key store for E2E file delivery (TASK-UI-VFS-004-1).
 *
 * The requester's ECDH private key (non-extractable CryptoKey) is generated when the
 * AccessRequestDialog submits, but the file arrives much later, after the dialog is
 * unmounted. The key is therefore persisted per requestId in IndexedDB (`vnp-dsearch-keys`).
 * A non-extractable CryptoKey is structured-cloned by IndexedDB without its raw bytes ever
 * being readable by JS. Nothing here touches localStorage, and nothing is logged.
 *
 * Lifecycle: `clearAll()` runs on logout (authStore) and on `pagehide` (non-bfcache).
 * Storage is behind {@link KeyStorage} so tests can use the in-memory implementation
 * (fake-indexeddb is not a dependency).
 */

export const EPHEMERAL_DB_NAME = 'vnp-dsearch-keys';
const STORE_NAME = 'keys';

export interface KeyStorage {
  put(requestId: string, key: CryptoKey): Promise<void>;
  get(requestId: string): Promise<CryptoKey | undefined>;
  delete(requestId: string): Promise<void>;
  clear(): Promise<void>;
}

export class MemoryKeyStorage implements KeyStorage {
  private readonly map = new Map<string, CryptoKey>();
  async put(id: string, key: CryptoKey) {
    this.map.set(id, key);
  }
  async get(id: string) {
    return this.map.get(id);
  }
  async delete(id: string) {
    this.map.delete(id);
  }
  async clear() {
    this.map.clear();
  }
}

function reqToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export class IndexedDbKeyStorage implements KeyStorage {
  private dbPromise: Promise<IDBDatabase> | null = null;

  private db(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = new Promise((resolve, reject) => {
        const open = indexedDB.open(EPHEMERAL_DB_NAME, 1);
        open.onupgradeneeded = () => {
          open.result.createObjectStore(STORE_NAME);
        };
        open.onsuccess = () => resolve(open.result);
        open.onerror = () => reject(open.error);
      });
      this.dbPromise.catch(() => {
        this.dbPromise = null;
      });
    }
    return this.dbPromise;
  }

  private async run<T>(
    mode: IDBTransactionMode,
    fn: (s: IDBObjectStore) => IDBRequest<T>
  ): Promise<T> {
    const db = await this.db();
    return reqToPromise(fn(db.transaction(STORE_NAME, mode).objectStore(STORE_NAME)));
  }

  async put(id: string, key: CryptoKey) {
    await this.run('readwrite', s => s.put(key, id));
  }
  async get(id: string) {
    return (await this.run('readonly', s => s.get(id))) as CryptoKey | undefined;
  }
  async delete(id: string) {
    await this.run('readwrite', s => s.delete(id));
  }
  async clear() {
    await this.run('readwrite', s => s.clear());
  }
}

export interface EphemeralKeystore {
  save(requestId: string, privateKey: CryptoKey): Promise<void>;
  load(requestId: string): Promise<CryptoKey | null>;
  remove(requestId: string): Promise<void>;
  clearAll(): Promise<void>;
}

/** All operations swallow storage errors (private mode, blocked IDB): never throw to UI. */
export function createEphemeralKeystore(storage: KeyStorage): EphemeralKeystore {
  return {
    async save(requestId, privateKey) {
      try {
        await storage.put(requestId, privateKey);
      } catch {
        // storage unavailable: caller falls back to in-memory handling
      }
    },
    async load(requestId) {
      try {
        return (await storage.get(requestId)) ?? null;
      } catch {
        return null;
      }
    },
    async remove(requestId) {
      try {
        await storage.delete(requestId);
      } catch {
        // ignore
      }
    },
    async clearAll() {
      try {
        await storage.clear();
      } catch {
        // ignore
      }
    },
  };
}

function defaultStorage(): KeyStorage {
  return typeof indexedDB !== 'undefined'
    ? new IndexedDbKeyStorage()
    : new MemoryKeyStorage();
}

let singleton: EphemeralKeystore | null = null;
let pagehideInstalled = false;

/** Replace the process-wide store (tests). Pass null to reset. */
export function setEphemeralKeystoreForTests(ks: EphemeralKeystore | null): void {
  singleton = ks;
}

export function getEphemeralKeystore(): EphemeralKeystore {
  if (!singleton) singleton = createEphemeralKeystore(defaultStorage());
  installPagehideCleanup();
  return singleton;
}

/** Wipe every stored key. Safe to call anywhere (logout). */
export function clearAllEphemeralKeys(): Promise<void> {
  return getEphemeralKeystore().clearAll();
}

function installPagehideCleanup(): void {
  if (pagehideInstalled || typeof window === 'undefined') return;
  pagehideInstalled = true;
  window.addEventListener('pagehide', (e: PageTransitionEvent) => {
    if (e.persisted) return; // bfcache: page may be restored
    void singleton?.clearAll();
  });
}
